/**
 * Tree widget
 * @url https://www.w3.org/TR/2013/WD-wai-aria-practices-20130307/#TreeView
 * @url http://oaa-accessibility.org/example/41/
 */

// Imports
import KeyCodes from '../KeyCodes';
import { isRTL } from '../Utils';
import TabManager from './TabManager';

// Constants (selectors)
const tree = '[data-js="tree"]';
const presentationItem = '[role="presentation"]';
const treeItem = '[role="treeitem"]';
const contentGroup = '[role="group"]';

// Constants (classes)
const activeClass = 'active';

const panelRenderEvent = 'panel-render';

const transitionProps = {
  slx: 'translateX',
  sly: 'translateY'
};

const allowedMode = {
  panel: 'panel'
};

const defaultParams = {
  displayMode: '',
  transition: ''
};

const delay = 300;

/**
 * Gets all the tabs for the given container.
 * @param  {Object} aoContainer - The container.
 * @return {Object} Either an empty object or a jQuery object with the found tabs.
 */
function getAllTabs(aoContainer) {
  const $container = $(aoContainer);
  if (!$container.length) {
    return {};
  }

  let $tabs = $container.children(treeItem);
  const $children = $container.children(`${treeItem},${presentationItem}`);

  // Check for other tree items
  $children.each((anIndex, aoElement) => {
    if ($(aoElement).is(presentationItem)) {
      const $tab = $(aoElement).children(treeItem).first();
      if ($tab.length) {
        $tabs = $tabs.add($tab);
      }
    }
  });

  return $tabs;
}

/* Use to animate transform property via jQuery */
function slide($elm, props, isLoop) {
  // clone the array (before "animate()" modifies it), and reverse it
  const reversed = JSON.parse(JSON.stringify(props)).reverse();

  $(props[0].lbd).animate(props[1].lbd, {
    duration: props[0].time.d,
    step: (val) => {
      $elm.css('transform', `${props[0].trans.p}(${val}%)`);
    },
    done: () => {
      if (isLoop) {
        slide($elm, reversed, false);
      }
    }
  });
}

function displayPanel(aoUI, cssProp) {
  const $tab = aoUI.toTab;
  const $root = $tab.parents('[role=tree]');
  const $relative = $tab.parent().closest('[role=treeitem]');
  const toRoot = $tab.parent('[role=tree]').length > 0;
  const isClimb = $tab.is('[aria-expanded=true]');
  const tStart = { lbd: { x: 0 }, time: { d: 0 }, trans: { p: cssProp } };
  const tEnd = { lbd: { x: 0 }, time: { d: delay }, trans: { p: cssProp } };

  $tab.prevAll().toggle();
  $tab.nextAll().toggle();

  $tab.toggleClass('m-panel--current');

  if (!toRoot) {
    $relative.toggleClass('m-panel--current m-panel--hidden');
  }

  if (isClimb) { // Handle climbing navigation tree
    tEnd.lbd.x = isRTL() ? 100 : -100;

    slide($root, [
      tStart,
      tEnd
    ], true);
  } else { // Handle digging navigation tree
    tEnd.lbd.x = isRTL() ? -100 : 100;

    slide($root, [
      tStart,
      tEnd
    ], true);
  }

  $(document).trigger(panelRenderEvent, [$root, (toRoot && isClimb)]);
}

function buildConfig(options) {
  const conf = JSON.parse(JSON.stringify(defaultParams)); // Simple JS object deep cloning

  if (typeof options !== 'undefined') {
    options.split(' ').forEach((opt) => {
      if (typeof allowedMode[opt] !== 'undefined') {
        conf.displayMode = allowedMode[opt];
      } else if (typeof transitionProps[opt] !== 'undefined') {
        conf.transition = transitionProps[opt];
      }
    });
  }

  return conf;
}

function resetContainer($container) {
  const $tree = $container.is('[role=tree]') ? $container : $container.children('[role=tree]');

  if ($tree.length) {
    $tree.find('.active').removeClass('active');
    $tree.find('[aria-hidden=false]').attr('aria-hidden', true);
    $tree.find('[aria-expanded=true]').attr({
      'aria-selected': false,
      'aria-expanded': false
    }).each((index, element) => {
      const $tab = $(element);

      TabManager.toggleIcon($tab);

      $tab.prevAll().show();
      $tab.nextAll().show();
    });

    if ($tree.data('bhv').indexOf('panel') !== -1) {
      $tree.find('.m-panel--current').removeClass('m-panel--current');
      $tree.find('.m-panel--hidden').removeClass('m-panel--hidden');
    }
  }
}

/**
 * Init handler of a tree.
 * @param {Object} params Parameters
 * @return {null|boolean} Returns false to break each loop.
 */
function containerInitHandler(params) {
  const $container = $(this);
  const $tabs = getAllTabs($container);
  const fParams = (typeof params !== 'undefined' && params) ? params : buildConfig($container.data('bhv'));

  if (!$tabs.length) {
    return; // == continue jquery each
  }

  const $groups = $tabs.children(contentGroup);
  const tabManager = new TabManager($tabs);

  /**
   * Set a tab active.
   * @param {jQuery} tab Tab element.
   * @return {Object} Plain object:
   *            toTab (jQuery)
   *            toContent (jQuery)
   * Else returns false if error.
   */
  function setActive($tab) {
    const $target = $(`#${$tab.attr('aria-controls')}`);
    const $toContent = $target.filter(contentGroup);

    // content found
    // already active ?
    if ($toContent.length && $toContent.hasClass(activeClass)) {
      $toContent.attr('aria-hidden', false);

      return false;
    }

    // active new tab
    $tab.attr({
      'aria-expanded': true,
    }).addClass(activeClass);

    // show new content
    tabManager.showContent($toContent);

    return {
      toTab: $tab,
      toContent: $toContent,
    };
  }

  /**
   * Toggle a tab active.
   * @param {jQuery} tab Tab element.
   * @return {Object} Plain object:
   *            toTab (jQuery)
   *            toContent (jQuery)
   * Else returns false if error.
   */
  function toggleActive(aoUI) {
    const $tab = aoUI.toTab;
    const $toContent = aoUI.toContent;

    // Maintain selected state
    tabManager.setSelected($tab);

    // already active ?
    if ($toContent.hasClass(activeClass)) {
      // inactivate
      $tab.attr({
        'aria-expanded': false,
      }).removeClass(activeClass);

      tabManager.hideContent($toContent);
    } else {
      // activate
      $tab.attr({
        'aria-expanded': true,
      }).addClass(activeClass);

      tabManager.showContent($toContent);
    }

    return {
      toTab: $tab,
      toContent: $toContent,
    };
  }

  /**
   * Focus handler for a tab.
   */
  function focusTabHandler() {
    tabManager.setSelected($(this));
  }

  function stateChangeHandler($tab) {
    const $target = $(`#${$tab.attr('aria-controls')}`);
    const ui = {
      toTab: $tab,
      toContent: $target.filter(contentGroup)
    };

    if (fParams.displayMode === 'panel') {
      displayPanel(ui, fParams.transition);
    }

    return ui;
  }

  /**
   * Click tab handler.
   * @param  {Event}   aoEvent - Event object.
   * @return {Boolean} Returns false when preventing default.
   */
  function clickTabHandler(aoEvent) {
    const $tab = $(this);

    if ($tab.length && !$tab.is('a')) {
      const ui = stateChangeHandler($tab);

      toggleActive(ui);

      TabManager.toggleIcon($tab);

      aoEvent.preventDefault();

      return false;
    }

    aoEvent.stopPropagation();

    return true;
  }

  /**
   * Keydown handler.
   * @param  {Event} aoEvent - Event object.
   * @return {Boolean} Returns false when preventing default.
   */
  function keydownTabHandler(aoEvent) {
    const $tab = $(this);

    if (aoEvent.altKey) {
      // do nothing
      return true;
    }

    let $targetTab = {};
    let $groupNode = {};
    let $parentNode = {};
    let $childNode = {};
    let $childNodes = {};

    switch (aoEvent.keyCode) {
      case KeyCodes.enter: {
        aoEvent.stopPropagation();

        if ($tab.is('a')) {
          return true;
        }

        return false;
      }
      case KeyCodes.left: {
        if ($tab.hasClass(activeClass) && ($tab.attr('aria-expanded') === 'true')) {
          // Left arrow key on an expanded node closes the node.

          toggleActive($tab);

          TabManager.toggleIcon($tab);

          aoEvent.stopPropagation();

          return false;
        }

        // Left arrow key on a closed or end node moves focus to the node's parent.

        $parentNode = $tab.closest(contentGroup);
        if ($parentNode.length) {
          $parentNode = $parentNode.closest(treeItem);
          if ($parentNode.length) {
            $targetTab = $parentNode;
          }
        }

        break;
      }
      case KeyCodes.right: {
        if (!$tab.hasClass(activeClass) && ($tab.attr('aria-expanded') === 'false')) {
          // Right arrow key expands a closed node.

          $groupNode = $tab.children(contentGroup).first();
          if ($groupNode.length) {
            toggleActive($tab);

            TabManager.toggleIcon($tab);
          }

          aoEvent.stopPropagation();

          return false;
        }

        if ($tab.hasClass(activeClass) && ($tab.attr('aria-expanded') === 'true')) {
          // Right arrow key moves to the first child of an open node.

          $groupNode = $tab.children(contentGroup).first();
          if ($groupNode.length) {
            $childNode = $groupNode.find(treeItem).first();
            if ($childNode.length) {
              // Move to the first child node
              $targetTab = $childNode;
            }
          }
        }

        break;
      }
      case KeyCodes.up: {
        // Up arrow key moves up between visible nodes.

        $targetTab = tabManager.getPreviousTab($tab);
        if (!$targetTab.length) {
          // No target tab => See if any parent tree item

          $parentNode = $tab.closest(contentGroup);
          if ($parentNode.length) {
            $parentNode = $parentNode.closest(treeItem);
            if ($parentNode.length) {
              // Move to the parent tree item
              $targetTab = $parentNode;
            }
          }
        } else if ($targetTab.hasClass(activeClass) && ($targetTab.attr('aria-expanded') === 'true')) {
          // Target tab is an open node

          const moveUpToLastChild = (aoTab) => {
            $groupNode = $(aoTab).children(contentGroup).first();
            if ($groupNode.length) {
              $childNodes = getAllTabs($groupNode);
              if ($childNodes.length) {
                // Move to the last child node of an expanded tree item

                $targetTab = $childNodes.last();
                if ($targetTab.hasClass(activeClass) && ($targetTab.attr('aria-expanded') === 'true')) {
                  // Recursive call needed
                  moveUpToLastChild($targetTab);
                }
              }
            }
          };

          // Recursive function call to get
          // the last child node up the tree
          moveUpToLastChild($targetTab);
        }

        break;
      }
      case KeyCodes.down: {
        // Down arrow key moves down between visible nodes.

        if ($tab.hasClass(activeClass) && ($tab.attr('aria-expanded') === 'true')) {
          // Current tab is an open node

          $groupNode = $tab.children(contentGroup).first();
          if ($groupNode.length) {
            $childNode = $groupNode.find(treeItem).first();
            if ($childNode.length) {
              // Move to the first child node
              $targetTab = $childNode;
            }
          }
        } else {
          $targetTab = tabManager.getNextTab($tab);
          if (!$targetTab.length) {
            // No target tab => See if any parent tree item

            $parentNode = $tab.parents(treeItem);
            $parentNode.each((anIndex, aoElement) => {
              // See if the parent node has a next tree item sibling

              $targetTab = $(aoElement).next(treeItem);
              if ($targetTab.length) {
                // Move to the next tree item sibling

                return false; // break
              }

              // See if the parent node has a next presentation item sibling

              $targetTab = $(aoElement).next(presentationItem);
              if ($targetTab.length) {
                // Move to the first tree item child node

                $targetTab = $targetTab.children(treeItem).first();

                return false; // break
              }

              return true; // continue
            });
          }
        }

        break;
      }
      case KeyCodes.home: {
        $targetTab = $tabs.first();

        break;
      }
      case KeyCodes.end: {
        $targetTab = $tabs.last();

        break;
      }
      default: {
        break;
      }
    }

    if ($targetTab.length) {
      $targetTab.focus();

      aoEvent.stopPropagation();

      return false;
    }

    return true;
  }

  function initAriaTabContent() {
    const $tab = $(this);
    const $target = $(`#${$tab.attr('aria-controls')}`);

    tabManager.hideContent($target, true);
  }

  function initAria() {
    $tabs.attr({
      'aria-selected': false,
      'aria-expanded': false,
      tabindex: -1,
    });
    $tabs.first().attr('tabindex', 0);
    $tabs.each(initAriaTabContent);
  }

  /**
   * Active an default tab at init
   */
  function initActive() {
    const $activeTabs = $tabs.filter(`.${activeClass}`);

    if ($activeTabs.length) {
      $activeTabs.each(function activeTabEach() {
        setActive($(this));
      });
    }
  }

  /**
   * Bind events
   */
  function initBinds() {
    $tabs.on('focus', focusTabHandler);
    $tabs.on('click', clickTabHandler);
    $tabs.keydown(keydownTabHandler);
  }

  initAria();
  initActive();
  initBinds();

  $container.trigger('loaded.tab');

  // Check for subtrees
  $groups.each(function eachGroup() {
    // Recursive call to handle the subtree
    containerInitHandler.call(this, fParams);
  });
}

/**
 * Init
 */
function init() {
  $(tree).each(containerInitHandler);
}

/**
 * Create feature on element.
 * @param  {jQuery|String} aoContainer - Container for the tree.
 * @param  {Object} aoOptions          - A set of key/value pairs:
 *    change (function): callback with event, ui arguments.
 *        ui properties are:
 *            toTab (jQuery)
 *            toContent (jQuery)
 */
function create(aoContainer, aoOptions) {
  const $container = $(aoContainer);

  $container.each(function eachInit() {
    containerInitHandler.call(this, aoOptions);
  });
}

export {
  init,
  create,
  resetContainer,
  panelRenderEvent
};

export default init;



// WEBPACK FOOTER //
// ./js/common/widgets/Tree.js